import { DateTime } from 'luxon';
import { REGIONS } from '../constants/region.constants';
import { MS_PER_DAY } from '../constants/date.constants';

export const DEFAULT_LOCALE = `en-${REGIONS.US}`;
export const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * Check if date is today
 * @param {*} date
 * @returns true if date is today
 * @example isToday('2022-01-19T14:29:00.000Z') => true
 */
const isToday = date => {
  const today = new Date();
  const dateToCheck = new Date(date);
  return (
    today.getDate() === dateToCheck.getDate() &&
    today.getMonth() === dateToCheck.getMonth() &&
    today.getFullYear() === dateToCheck.getFullYear()
  );
};

/**
 * Check if date is tomorrow
 * @param {*} date
 * @returns true if date is tomorrow
 * @example isTomorrow('2022-01-19T14:29:00.000Z') => true
 */
const isTomorrow = date => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dateToCheck = new Date(date);
  return (
    tomorrow.getDate() === dateToCheck.getDate() &&
    tomorrow.getMonth() === dateToCheck.getMonth() &&
    tomorrow.getFullYear() === dateToCheck.getFullYear()
  );
};

/**
 * Check if date is in the past
 * @param {*} date
 * @returns true if date is in the past
 * @example isPastDate('2022-01-19T14:29:00.000Z') => false
 */
const isPastDateOrToday = date => {
  const today = new Date();
  const dateToCheck = new Date(date);
  return today >= dateToCheck;
};

/**
 * Calculate days until date
 * @param {*} date
 * @returns days until date
 * @example calculateDaysUntil('2022-01-19T14:29:00.000Z') => 10
 */
const calculateDaysUntil = date => {
  if (!date) {
    return null;
  }

  if (isPastDateOrToday(date)) {
    return null;
  }

  const today = new Date();
  const dateToCheck = new Date(date);
  const diffTime = dateToCheck - today;
  const diffDays = Math.ceil(diffTime / MS_PER_DAY);

  return diffDays;
};

/**
 * Check if date is in the future
 * @param {*} date
 * @returns true if date is in the future
 * @example isFutureDate('2022-01-19T14:29:00.000Z') => true
 */
const isFutureDate = date => {
  const today = new Date();
  const dateToCheck = new Date(date);
  return today < dateToCheck;
};

/**
 * Format date in long format, locale dependent
 * @param {*} date
 * @param {*} options
 * @returns date in long format
 */
const formatDateLocale = ({ date, options, locale = DEFAULT_LOCALE }) => {
  return Intl.DateTimeFormat(locale, options).format(date);
};

/**
 * Format date and time in long format, locale dependent
 * @param {*} date
 * @param {*} options
 * @param {*} locale
 * @returns date and time in long format
 */
const formatDateTimeLocale = ({ date, options, locale = DEFAULT_LOCALE }) => {
  return Intl.DateTimeFormat(locale, options).format(date);
};

/**
 * Format date range in locale dependent
 * @param {*} start
 * @param {*} end
 * @param {*} options
 * @param {*} locale
 * @returns date range in locale dependent
 */
// TODO: formatRange doesn't work in Android, investigate alternative
// const formatDateRangeLocale = ({ start, end, options, locale = DEFAULT_LOCALE }) => {
//   return Intl.DateTimeFormat(locale, options).formatRange(start, end);
// };

/**
 * Format date in long format
 * @param {*} date
 * @param {*} locale
 * @returns date in long format
 * @example "Jan 19, 2022"
 */
const formatLongDate = ({ date, locale, options }) => {
  if (!date) {
    return '';
  }
  return formatDateLocale({
    date: new Date(date),
    options: { year: 'numeric', month: 'short', day: 'numeric', ...options },
    locale,
  });
};

/**
 * Format date and time in long format
 * @param {*} date
 * @param {*} locale
 * @returns date and time in long format
 * @example "January 19, 2022 at 2:29 PM"
 */
const formatLongDateTime = ({ date, locale, options }) => {
  if (!date) {
    return '';
  }
  return formatDateTimeLocale({
    date: new Date(date),
    options: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      ...options,
    },
    locale,
  });
};

/**
 * Format date in short format
 * @param {*} date
 * @param {*} locale
 * @returns date in short format, with year if not current year
 * @example "1/19/22"
 */
const formatShortDate = ({ date, locale, options }) => {
  if (!date) {
    return '';
  }
  const currentYear = new Date().getFullYear();
  const dateYear = new Date(date).getFullYear();

  return formatDateLocale({
    date: new Date(date),
    options: {
      year: currentYear !== dateYear ? 'numeric' : undefined,
      month: 'numeric',
      day: 'numeric',
      ...options,
    },
    locale,
  });
};

/**
 * Format date and time in short format
 * @param {*} date
 * @param {*} locale
 * @returns date and time in short format, with year if not current year
 * @example "1/19/22, 2:31 PM"
 */
const formatShortDateTime = ({ date, locale, options }) => {
  if (!date) {
    return '';
  }
  const currentYear = new Date().getFullYear();
  const dateYear = new Date(date).getFullYear();

  return formatDateTimeLocale({
    date: new Date(date),
    options: {
      year: currentYear !== dateYear ? 'numeric' : undefined,
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      ...options,
    },
    locale,
  });
};

/**
 * Format date range in short format
 * @param {*} start
 * @param {*} end
 * @param {*} locale
 * @returns date range in short format, with year if not current year
 * @example "1/19/22 - 1/21/22"
 */
const formatRangeDate = ({ start, end, locale, options }) => {
  const startDate = formatShortDate({
    date: new Date(start),
    locale,
    options,
  });
  const endDate = formatShortDate({
    date: new Date(end),
    locale,
    options,
  });

  return `${startDate} - ${endDate}`;
};

/**
 * Format time in short format
 * @param {*} date
 * @returns time in short format
 * @example "2:31 PM"
 */
const formatShortTime = ({ date, locale, options }) => {
  if (!date) {
    return '';
  }
  return formatDateTimeLocale({
    date: new Date(date),
    options: { hour: 'numeric', minute: 'numeric', ...options },
    locale,
  });
};

/**
 * Format date and time with abbreviated month
 * @param {*} date
 * @param {*} locale
 * @returns date and time with abbreviated month
 * @example "Mar 3, 2025, 9:52 AM"
 */
const formatMediumDateTime = ({ date, locale, options }) => {
  if (!date) {
    return '';
  }
  return formatDateTimeLocale({
    date: new Date(date),
    options: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      ...options,
    },
    locale,
  });
};

/**
 * Returns the short timezone abbreviation (e.g. "EST"/"EDT") for a date in a
 * given IANA timezone. Resolved against the *event's* instant, so DST
 * transitions are honored — the same zone yields "EST" in January and "EDT" in
 * July. Zones the locale has no abbreviation for get the offset form instead —
 * "GMT+3" in en, "UTC+3" in fr.
 * @param {*} date
 * @param {string} timeZone IANA zone, e.g. "America/New_York"
 * @param {string} locale
 * @returns {string} abbreviation, or '' when inputs are missing
 */
const getTimezoneAbbr = ({ date, timeZone, locale = DEFAULT_LOCALE }) => {
  if (!date || !timeZone) {
    return '';
  }
  const parts = Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' }).formatToParts(
    new Date(date),
  );
  const name = parts.find(part => part.type === 'timeZoneName')?.value ?? '';
  const offsetPrefix = name.match(/^(?:GMT|UTC)/)?.[0];
  return offsetPrefix ? formatOffsetTimezoneName({ date, timeZone, prefix: offsetPrefix }) : name;
};

/**
 * Returns the UTC offset (in minutes) that `timeZone` is at on the given date.
 * Unlike a "now" offset, this is evaluated at the event's instant, so it stays
 * correct across DST boundaries. East-of-UTC zones are positive.
 * @param {*} date
 * @param {string} timeZone IANA zone
 * @returns {number} offset in minutes
 */
const getTimezoneOffsetMinutes = ({ date, timeZone }) =>
  DateTime.fromJSDate(new Date(date), { zone: timeZone }).offset;

/**
 * Renders a timezone's offset against the given prefix, dropping the minutes
 * when they are zero — "GMT+3", "GMT+5:45", "UTC-4", or the bare prefix at a
 * zero offset (London in winter).
 * @param {*} date
 * @param {string} timeZone IANA zone
 * @param {string} prefix "GMT" or "UTC", per the locale
 * @returns {string}
 */
const formatOffsetTimezoneName = ({ date, timeZone, prefix }) => {
  const offsetMinutes = getTimezoneOffsetMinutes({ date, timeZone });
  if (!offsetMinutes) {
    return prefix;
  }
  const absMinutes = Math.abs(offsetMinutes);
  const minutes = absMinutes % 60;
  const offset = minutes
    ? `${Math.floor(absMinutes / 60)}:${String(minutes).padStart(2, '0')}`
    : `${absMinutes / 60}`;
  return `${prefix}${offsetMinutes < 0 ? '-' : '+'}${offset}`;
};

/**
 * Whether two IANA timezones share the same UTC offset *on the given date*.
 * Compares at the event's instant so a device/org pair that only diverges
 * during one side's DST window is detected correctly.
 * @param {*} date
 * @param {string} timeZoneA
 * @param {string} timeZoneB
 * @returns {boolean}
 */
const isSameOffsetAtDate = ({ date, timeZoneA, timeZoneB }) => {
  if (!date || !timeZoneA || !timeZoneB) {
    return true;
  }
  return (
    getTimezoneOffsetMinutes({ date, timeZone: timeZoneA }) ===
    getTimezoneOffsetMinutes({ date, timeZone: timeZoneB })
  );
};

/**
 * Convert an exclusive end date (next-day midnight) to an inclusive end date
 * for display purposes. Rotation end dates are stored as exclusive — subtracting
 * one day gives the last active day.
 * @param {string | Date} date
 * @returns {Date}
 */
const toInclusiveEndDate = date => {
  if (!date) {
    return '';
  }

  const d = new Date(date);
  d.setDate(d.getDate() - 1);
  return d;
};

/**
 * Local midnight today.
 * @returns {Date}
 */
const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/**
 * ISO timestamp of local midnight `days` ago.
 * @param {number} days
 * @returns {string}
 */
const daysAgoIso = days => {
  const date = startOfToday();
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

export {
  isToday,
  isTomorrow,
  startOfToday,
  daysAgoIso,
  isPastDateOrToday,
  isFutureDate,
  calculateDaysUntil,
  formatLongDate,
  formatLongDateTime,
  formatShortDate,
  formatShortDateTime,
  formatMediumDateTime,
  formatRangeDate,
  formatShortTime,
  formatDateLocale,
  getTimezoneAbbr,
  getTimezoneOffsetMinutes,
  isSameOffsetAtDate,
  toInclusiveEndDate,
};
